<script setup lang="ts">
const { resetAuthModal } = useCasinoStore();
const { authModalGames } = storeToRefs(useCasinoStore());
const { isAuthenticated } = storeToRefs(useAuthStore());
const { getThumbnailPathSet, normalizeThumbnail } = useCasino();

const thumbnailPathSet = computed<string>(() => {
  return getThumbnailPathSet(authModalGames.value.image);
});

const thumbnailPath = computed<string>(() => {
  return `${normalizeThumbnail(authModalGames.value.image)}/public`;
});
</script>

<template>
  <Teleport to="body">
    <section
      v-if="!isAuthenticated"
      :class="[
        $style.casinoAuthSection,
        { [$style.opened]: authModalGames.open },
      ]"
    >
      <button
        :class="$style.closeButton"
        @click="resetAuthModal()"
      >
        <NuxtIcon name="close" />
      </button>

      <div :class="$style.casinoAuthContent">
        <div :class="$style.casinoAuthBody">
          <div :class="$style.casinoAuthGame">
            <div :class="$style.casinoAuthImage">
              <div
                v-if="!authModalGames.image"
                :class="[`skeleton-loading`, $style.skeleton]"
              />

              <NuxtImg
                v-else
                :alt="authModalGames.title || 'Entre para jogar'"
                height="100"
                sizes="(max-width: 600px) 200px, 50vw"
                :src="thumbnailPath"
                :srcset="thumbnailPathSet || undefined"
                width="100"
              />
            </div>

            <div :class="$style.casinoAuthInfo">
              <h3>
                <div
                  v-if="!authModalGames.title"
                  :class="[`skeleton-loading`, $style.skeleton]"
                />

                <span v-else>
                  {{ authModalGames.title }}
                </span>
              </h3>

              <h4>
                <div
                  v-if="!authModalGames.subtitle"
                  :class="[`skeleton-loading`, $style.skeleton]"
                />

                <span v-else>
                  {{ authModalGames.subtitle }}
                </span>
              </h4>

              <div :class="$style.casinoAuthButtons">
                <HeaderButtons />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </Teleport>
</template>

<style module lang="scss">
.casinoAuthSection {
  @apply fixed w-full h-full bottom-0 left-0 z-[800] flex flex-col items-center justify-end invisible opacity-0 select-none md:hidden;

  .closeButton {
    @apply relative w-full h-full p-2.5 flex justify-end items-end text-white bg-bg-primary/70 text-2xl;
  }

  .casinoAuthContent {
    @apply w-full bg-bg-primary;

    box-shadow: 0 0 2rem rgba(0, 0, 0, 0.4);

    .casinoAuthBody {
      @apply flex flex-col items-start justify-end py-6 px-4 max-w-md my-0 mx-auto;

      .casinoAuthGame {
        @apply flex gap-4 w-full  grid-cols-2;

        .casinoAuthImage {
          @apply relative w-full max-w-[150px] flex flex-col items-center rounded overflow-hidden;

          .skeleton {
            @apply w-full h-full;
          }

          img {
            @apply object-cover object-top w-full h-full rounded-lg;
          }
        }

        .casinoAuthInfo {
          @apply flex flex-col justify-center px-2;

          h3 {
            @apply text-texts w-full text-lg font-bold m-0;

            .skeleton {
              @apply w-full h-5;
            }
          }

          h4 {
            @apply text-texts/70 w-full text-sm font-normal opacity-70;

            .skeleton {
              @apply w-3/5 h-5 mt-2;
            }
          }

          .casinoAuthButtons {
            @apply mt-6;
          }
        }
      }
    }
  }

  &.opened {
    @apply opacity-100 visible;

    .casinoAuthContent {
      transform: translateY(0%);
    }

    .casinoAuthBody {
      .casinoAuthGame {
        .casinoAuthImage {
          opacity: 1;
        }
      }
    }
  }
}
</style>
